import { clearAllCaches } from '@/utils/cache';
import Keycloak from 'keycloak-js';
import type { App } from 'vue';
import type { Router } from 'vue-router';

// Keycloak instance - will be set based on runtime mode
let keycloak: Keycloak;

// Token refresh interval (5 minutes before expiry)
let tokenRefreshInterval: number | null = null;

// BroadcastChannel for multi-tab logout synchronization
const authChannel = new BroadcastChannel('auth_channel');

// Setup listener for logout messages from other tabs
authChannel.onmessage = (event) => {
  if (event.data === 'logout') {
    console.log('Logout signal received from another tab');
    if (tokenRefreshInterval) {
      clearInterval(tokenRefreshInterval);
    }
    window.location.reload();
  }
};

// Setup automatic token refresh
const setupTokenRefresh = () => {
  if (tokenRefreshInterval) {
    clearInterval(tokenRefreshInterval);
  }

  tokenRefreshInterval = setInterval(() => {
    keycloak
      .updateToken(300) // Refresh if token expires in 5 minutes
      .then((refreshed: boolean) => {
        if (refreshed) {
          console.log('Token refreshed');
        }
      })
      .catch(() => {
        console.error('Failed to refresh token');
        keycloak.logout();
      });
  }, 60000); // Check every 1 minute
};

const setupRouteGuards = (router?: Router) => {
  // Setup route guards if router is provided
  if (router) {
    router.beforeEach((to, _from, next) => {
      // Check if route requires authentication
      if (to.meta.requiresAuth && !keycloak.authenticated) {
        // Redirect to Keycloak login
        keycloak.login({
          redirectUri: window.location.origin + to.path,
        });
      } else {
        next();
      }
    });
  }
};

// Initialize Keycloak - call this before creating Vue app
export const initKeycloak = async (
  router?: Router,
  rootKeycloakInstance?: Keycloak,
) => {
  try {
    // Determine runtime mode and setup Keycloak instance
    const isStandalone = process.env.VUE_APP_IS_STANDALONE === 'true';

    if (isStandalone) {
      // Keycloak configuration
      const keycloakConfig = {
        url: `https://${process.env.VUE_APP_SSO_DOMAIN}/auth`,
        realm: process.env.VUE_APP_MERCHANT_ID,
        clientId: process.env.VUE_APP_KEYCLOAK_CLIENT_ID,
      };

      // Standalone mode - create our own Keycloak instance
      console.log('Initializing Keycloak in standalone mode');
      keycloak = new Keycloak(keycloakConfig);

      const authenticated = await keycloak.init({
        onLoad: 'login-required',
        pkceMethod: 'S256',
        checkLoginIframe: false,
      });

      if (authenticated) {
        console.log('User is authenticated in standalone mode');
        setupTokenRefresh();
      } else {
        console.log('User is not authenticated in standalone mode');
      }

      setupRouteGuards(router);

      return authenticated;
    } else {
      // Single-SPA mode - use Keycloak instance from root-config
      console.log('Initializing Keycloak in single-spa mode');

      if (!rootKeycloakInstance) {
        throw new Error(
          'Keycloak instance not provided by root-config. Expected rootKeycloakInstance parameter.',
        );
      }

      keycloak = rootKeycloakInstance;

      // Check if already authenticated
      const authenticated = keycloak.authenticated || false;

      if (authenticated) {
        console.log('User is authenticated via root-config');
      } else {
        console.log('User is not authenticated via root-config');
      }

      setupRouteGuards(router);

      return authenticated;
    }
  } catch (error) {
    console.error('Failed to initialize Keycloak', error);
    throw error;
  }
};

// Keycloak plugin - synchronous, only provides the instance
export const keycloakPlugin = {
  install: (app: App) => {
    // Make keycloak instance available globally
    app.config.globalProperties.$keycloak = keycloak;
    app.provide('keycloak', keycloak);
  },
};

// Export keycloak instance for direct access
export { keycloak };

// Helper functions
export const login = (redirectPath?: string) => {
  keycloak.login({
    redirectUri: window.location.origin + (redirectPath || '/'),
  });
};

export const logout = async (redirectPath?: string) => {
  if (tokenRefreshInterval) {
    clearInterval(tokenRefreshInterval);
  }
  // Clear all caches before logout to prevent data leakage between users
  await clearAllCaches();
  // Notify other tabs about logout
  authChannel.postMessage('logout');
  keycloak.logout({
    redirectUri: window.location.origin + (redirectPath || '/'),
  });
};

export const getToken = () => keycloak.token;

export const isAuthenticated = () => keycloak.authenticated;

export const hasRole = (role: string) => {
  return keycloak.hasRealmRole(role) || keycloak.hasResourceRole(role);
};

export const getUserInfo = () => keycloak.loadUserInfo();
